import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterList } from '@fortawesome/pro-light-svg-icons/faFilterList';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
import { PromoPerformanceTotals } from '../promote/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../promote/Promote.module.scss';
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PromoFunnelPanelProps {
  /** Undefined before the read. The API answers numbers; an old or broken answer can miss a key. */
  totals?: Partial<Record<keyof PromoPerformanceTotals, number | null>>;
}

/** A count of the funnel: a number (also 0), or null when the answer does not give it. */
function countOf(value: number | null | undefined): number | null {
  return typeof value === 'number' ? value : null;
}

/**
 * The funnel, with only the steps we can count filled in.
 *
 * Impressions, views and saves are the platforms' numbers, and no platform
 * hands them to us yet. Their rows stay, so the shape of the funnel is honest
 * about what is missing rather than hiding it.
 */
const FunnelValue: React.FC<{ intl: ReturnType<typeof useIntl>; value: number | null; testId: string }> = ({
  intl,
  value,
  testId,
}) =>
  value === null ? (
    <span className={clsx(styles.funnelValue, shared.dash)} data-testid={testId}>
      —
    </span>
  ) : (
    <span className={styles.funnelValue} data-testid={testId}>
      {intl.formatNumber(value)}
    </span>
  );

const PromoFunnelPanelComponent: React.FC<PromoFunnelPanelProps> = ({ totals }) => {
  const intl = useIntl();

  const waiting = intl.formatMessage({
    id: 'app.sml.promote.performance.funnel.waiting',
    defaultMessage: 'Arrives when the channel APIs connect.',
  });
  const clicksWaiting = intl.formatMessage({
    id: 'app.sml.promote.performance.funnel.clicksWaiting',
    defaultMessage: 'Arrives with click attribution.',
  });

  // A missing count is a dash, never 0 (owner rule 2026-10-18). A 0 is a real number.
  const posts = countOf(totals?.postsPublished);
  const clicks = countOf(totals?.linkClicks);

  const missing = useMemo(
    () => [
      intl.formatMessage({ id: 'app.sml.promote.performance.funnel.impressions', defaultMessage: 'Impressions' }),
      intl.formatMessage({ id: 'app.sml.promote.performance.funnel.views', defaultMessage: 'Views' }),
      intl.formatMessage({ id: 'app.sml.promote.performance.funnel.saves', defaultMessage: 'Saves' }),
    ],
    [intl]
  );

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faFilterList} />}
        title={intl.formatMessage({ id: 'app.sml.promote.performance.funnel', defaultMessage: 'Promo funnel' })}
      />
      <PanelBody>
        <div className={styles.funnel}>
          <div className={styles.funnelRow}>
            <div className={styles.funnelHead}>
              <span className={styles.funnelLabel}>
                {intl.formatMessage({ id: 'app.sml.promote.stat.postsPublished', defaultMessage: 'Posts published' })}
              </span>
              <FunnelValue intl={intl} value={posts} testId="promo-funnel-posts" />
            </div>
            <div className={styles.funnelTrack}>
              <div className={styles.funnelFill} style={{ width: posts === null || posts === 0 ? '0%' : '100%' }} />
            </div>
          </div>

          <div className={styles.funnelRow}>
            <div className={styles.funnelHead}>
              <span className={styles.funnelLabel}>
                {intl.formatMessage({ id: 'app.sml.promote.stat.linkClicks', defaultMessage: 'Link clicks' })}
                <FontAwesomeIcon className={shared.infoMark} icon={faCircleInfo} title={clicksWaiting} />
              </span>
              <FunnelValue intl={intl} value={clicks} testId="promo-funnel-clicks" />
            </div>
            <div className={styles.funnelTrack}>
              <div
                className={styles.funnelFill}
                style={{
                  width:
                    posts === null || clicks === null || posts === 0
                      ? '0%'
                      : `${Math.min(Math.round((clicks / posts) * 100), 100)}%`,
                }}
              />
            </div>
          </div>

          {missing.map((label) => (
            <div key={label} className={clsx(styles.funnelRow, styles.funnelWaiting)}>
              <div className={styles.funnelHead}>
                <span className={styles.funnelLabel}>{label}</span>
                <span className={clsx(styles.funnelValue, shared.dash)}>—</span>
              </div>
              <span className={styles.funnelWait}>{waiting}</span>
            </div>
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const PromoFunnelPanel = TypedMemo(PromoFunnelPanelComponent);
